/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Bell, BellOff } from 'lucide-react';
import usePushNotifications from '@/lib/usePushNotifications';
import { toast } from 'sonner';

export const PushNotificationsCard = () => {
  const { t } = useTranslation();
  const { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();

  const handleToggle = async () => {
    if (subscribed) {
      const ok = await unsubscribe();
      if (ok) toast.success(t('pushDisabled'));
    } else {
      const ok = await subscribe();
      if (ok) toast.success(t('pushEnabled'));
      else if (permission === 'denied') toast.error(t('pushBlocked'));
      else toast.error(t('errorOccurred'));
    }
  };

  const handleTest = async () => {
    const r = await sendTest();
    if (r.sent > 0) toast.success(t('pushTestSent'));
    else toast.error(t('pushTestNoDevice'));
  };

  return (
    <div className="rounded-xl border border-border p-4 bg-secondary/30 mt-3" data-testid="settings-push-card">
      <div className="flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: subscribed ? 'hsl(var(--primary) / 0.13)' : '#94A3B822', color: subscribed ? 'var(--wl-primary)' : '#64748B' }}
        >
          {subscribed ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{t('pushNotifications')}</div>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            {!supported
              ? t('pushNotSupported')
              : permission === 'denied'
                ? t('pushBlocked')
                : t('pushDesc')}
          </p>
          {supported && permission !== 'denied' && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={subscribed ? 'secondary' : 'default'}
                onClick={handleToggle}
                disabled={loading}
                data-testid={subscribed ? 'settings-push-disable' : 'settings-push-enable'}
                className="gap-1.5"
              >
                {subscribed ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                {subscribed ? t('pushDisable') : t('pushEnable')}
              </Button>
              {subscribed && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleTest}
                  data-testid="settings-push-test"
                >
                  {t('pushSendTest')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
